import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import { toast } from "react-toastify";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import DeleteModal from "components/common/DeleteModal";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import { CodeCell, IndexColumn } from "components/common/columns";
import { DateOnly } from "components/common/Date";
import { formatQuantity } from "utils/quantity";
import { getDocumentTitle } from "utils/helpers";
import {
  SOURCE_TYPE_URL_MAP,
  type AnySourceType,
} from "constants/source-type.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  deleteToolInOut,
  getToolInOuts,
  returnToolInOut,
} from "features/ToolInOuts/services/ToolInOutService";
import { ToolInOut } from "features/ToolInOuts/types/tool-in-out.types";
import { ToolInOutSourceType } from "features/ToolInOuts/constants/tool-in-out.constants";
import { ToolInOutCreateModal } from "../ToolInOutForm/ToolInOutCreateModal";
import ToolInOutListFilters, {
  countToolInOutListFiltersActive,
  emptyToolInOutListFilters,
  type ToolInOutListFiltersState,
} from "./ToolInOutListFilters";

/**
 * NOTE: this list's "Edit" link points at `/inventory-management/tool-in-outs/edit/:id`
 * (mirroring `ProductCategories/ProductCategoryList`'s edit-route pattern) —
 * a placeholder path pending the routes/sidebar wiring done by a separate
 * process; adjust once the real route segment is known.
 */
const EDIT_PATH_PREFIX = "/inventory-management/tool-in-outs/edit";

const ToolInOutList = () => {
  document.title = getDocumentTitle("Tool in/out");
  const { can } = useAccess();
  const { isSuperAdmin } = useProfile();
  const canCreate = can(Permissions.TOOL_IN_OUT.CREATE);
  const canUpdate = can(Permissions.TOOL_IN_OUT.UPDATE);
  const canDelete = can(Permissions.TOOL_IN_OUT.DELETE);
  const canReturn = can(Permissions.TOOL_IN_OUT.RETURN);

  const tableRef = useRef<() => void>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [selected, setSelected] = useState<ToolInOut | null>(null);
  const [filters, setFilters] = useState<ToolInOutListFiltersState>(
    emptyToolInOutListFilters(),
  );

  const requestParams = useMemo(
    () => ({
      outstandingOnly: filters.outstandingOnly || undefined,
      userId: filters.userId === "" ? undefined : filters.userId,
      sourceType: filters.sourceType || undefined,
      toolId: filters.toolId === "" ? undefined : filters.toolId,
    }),
    [filters],
  );

  const filterCount = countToolInOutListFiltersActive(filters);

  const openCreate = useCallback(() => setCreateModalOpen(true), []);
  const closeCreateModal = useCallback(() => setCreateModalOpen(false), []);

  const onClickDelete = useCallback((record: ToolInOut) => {
    setSelected(record);
    setDeleteOpen(true);
  }, []);

  const onClickReturn = useCallback((record: ToolInOut) => {
    setSelected(record);
    setReturnOpen(true);
  }, []);

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await deleteToolInOut(selected.id);
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
      }
      setDeleteOpen(false);
      setSelected(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? err?.message ?? "Delete failed",
      );
    }
  };

  const handleReturn = async () => {
    if (!selected) return;
    setReturnSubmitting(true);
    try {
      const res = await returnToolInOut(selected.id, {});
      if (res.success) {
        toast.success(res.message);
        tableRef.current?.();
        setReturnOpen(false);
        setSelected(null);
      } else {
        toast.error(res.message ?? "Could not mark as returned.");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
          err?.message ??
          "Could not mark as returned.",
      );
    } finally {
      setReturnSubmitting(false);
    }
  };

  const columns: ColumnDef<ToolInOut>[] = useMemo(
    () => [
      IndexColumn<ToolInOut>(),
      {
        header: "Reference No",
        accessorKey: "referenceNo",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.referenceNo?.trim() ? (
            <CodeCell code={row.original.referenceNo} />
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Tool",
        id: "tool",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.tool?.name ?? "—"}</span>
        ),
      },
      {
        header: "Concerned Person",
        id: "user",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const u = row.original.user;
          const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ");
          return name ? name : <span className="text-muted">—</span>;
        },
      },
      {
        header: "Work",
        id: "work",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const r = row.original;
          const label = r.workLabel?.trim();
          if (!label) return <span className="text-muted">—</span>;

          const canLink =
            r.sourceType !== ToolInOutSourceType.OTHER && r.sourceId != null;
          const base = canLink
            ? (SOURCE_TYPE_URL_MAP[r.sourceType as string as AnySourceType] ??
              null)
            : null;

          return base ? (
            <Link
              to={`${base}/${r.sourceId}`}
              className="text-decoration-none link"
            >
              {label}
            </Link>
          ) : (
            <span>{label}</span>
          );
        },
      },
      {
        header: "Quantity",
        accessorKey: "quantity",
        enableColumnFilter: false,
        cell: ({ row }) => formatQuantity(row.original.quantity),
      },
      {
        header: "Date Out",
        accessorKey: "dateOut",
        enableColumnFilter: false,
        cell: ({ row }) => <DateOnly date={row.original.dateOut} />,
      },
      {
        header: "Date In",
        id: "dateIn",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const dateIn = row.original.dateIn;
          if (dateIn == null) {
            return (
              <span className="badge bg-warning-subtle text-warning">
                OUT
              </span>
            );
          }
          return (
            <div className="d-flex flex-column align-items-start gap-1">
              <span className="badge bg-success-subtle text-success">
                RETURNED
              </span>
              <DateOnly date={dateIn} />
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const isOpen = r.dateIn == null;
          const showEdit = canUpdate && (isOpen || isSuperAdmin);
          const showReturn = canReturn && isOpen;
          const hasMenuItems = showEdit || showReturn || canDelete;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Tool in/out actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {showEdit ? (
                  <DropdownItem tag={Link} to={`${EDIT_PATH_PREFIX}/${r.id}`}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showReturn ? (
                  <DropdownItem onClick={() => onClickReturn(r)}>
                    <i className="ri-checkbox-circle-line align-bottom me-2 text-success" />
                    Mark returned
                  </DropdownItem>
                ) : null}
                {canDelete ? (
                  <>
                    {showEdit || showReturn ? <DropdownItem divider /> : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(r)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canUpdate, canDelete, canReturn, isSuperAdmin, onClickDelete, onClickReturn],
  );

  return (
    <React.Fragment>
      <ToolInOutCreateModal
        show={createModalOpen}
        editingRecord={null}
        onClose={closeCreateModal}
        onSuccess={() => tableRef.current?.()}
      />

      <DeleteModal
        show={deleteOpen}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setDeleteOpen(false);
          setSelected(null);
        }}
        recordId={selected?.tool?.name}
      />

      <ConfirmActionModal
        show={returnOpen}
        title="Mark as returned"
        iconClass="ri-checkbox-circle-line"
        iconColor="text-success"
        heading="Mark this tool as returned?"
        description={
          selected
            ? `${selected.tool?.name ?? "This tool"} will be marked returned as of now.`
            : undefined
        }
        confirmText="Mark returned"
        confirmColor="success"
        confirmDisabled={returnSubmitting}
        onConfirm={() => void handleReturn()}
        onClose={() => {
          setReturnOpen(false);
          setSelected(null);
        }}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Tool in/out" pageTitle="Inventory Management" />
          <Card>
            <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2 border-bottom">
              <div>
                <h5 className="card-title mb-1">Tool check-out ledger</h5>
                <p className="text-muted small mb-0">
                  Track which tool is checked out to whom, and against which
                  Work Order, Petty Work, AMC, or other reason.
                </p>
              </div>
              {canCreate ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openCreate}
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add
                </button>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getToolInOuts}
                requestParams={requestParams}
                pageSize={10}
                globalSearch
                SearchPlaceholder="Search by tool, reference no, work, or description…"
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-0"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                toolbarRight={
                  <ToolInOutListFilters
                    filters={filters}
                    onChange={setFilters}
                  />
                }
                filterCount={filterCount}
                onFiltersClear={() => setFilters(emptyToolInOutListFilters())}
              />
            </div>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ToolInOutList;
